import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInitialLoadingScreenManager } from '../../../hooks/useInitialLoadingScreenManager';
import { useViewport } from '../../../contexts/ViewportContext';
import type { Topic } from '../../../types/topic';
import { MobileTaskBoard } from './mobile/MobileTaskBoard';
import { logger } from '../../../utils/logger';
import { useTasks } from '../../../hooks/queries/useTasks';
import { useTaskColumns } from '../../../hooks/queries/useTaskColumns';
import { TaskTimelineView } from './timeline/TaskTimelineView';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndTaskBoard } from './dnd/DndTaskBoard';
import TaskFilterToolbar from './filters/TaskFilterToolbar';
import { UserFilter } from '../../dashboard/types/dashboardTypes';
import { TaskSkeleton } from './TaskSkeleton';
import { MobileTaskSkeleton } from './mobile/MobileTaskSkeleton';
import { listCollaborators } from '../../projects/services/projectCollaboratorService';
import { useTaskUIStore } from '../../../store/taskUIStore';
import { useTopicSubscription } from '../../../hooks/useTopicSubscription';
import { taskKeys } from '../../../hooks/queries/taskKeys';
import { taskColumnKeys } from '../../../hooks/queries/taskColumnKeys';
import {
  MinimalRealtimeFallback,
  RealtimeErrorBoundary,
} from '../../realtime/RealtimeErrorBoundary';
import { FormattedTask } from '../../../hooks/queries/taskTypes';
import {
  TopicFiltersProvider,
  useTopicFilters,
  useTopicFiltersCleanup,
} from '../../../contexts/TopicFiltersContext';

// View mode types
type ViewMode = 'board' | 'timeline';

interface TaskTopicProps {
  topic: Topic;
  hasCollaborators?: boolean;
  onBackClick: () => void;
}

// Inner component that uses the filter context
function TaskTopicContent({ topic, hasCollaborators, onBackClick }: TaskTopicProps) {
  const { isMobile } = useViewport();

  // Use React Query hooks for data fetching
  const { data: tasks = [], isLoading: isTasksLoading } = useTasks(topic.id);

  // Column state from React Query
  const { data: columns = [], isLoading: isColumnsLoading } = useTaskColumns(topic.id);

  // Get filters from context - clean and simple!
  const {
    searchQuery,
    selectedUserIds,
    selectedTags,
    showUnscheduledOnly,
    showUnassignedOnly,
    showHighlightedOnly,
    sortByDeadline,
    setSelectedUserIds,
    setSelectedTags,
    toggleUnscheduledOnly,
    toggleUnassignedOnly,
    toggleHighlightedOnly,
    toggleSortByDeadline,
    resetFilters,
  } = useTopicFilters();

  // Get mobile column state from UI store (not filter-related)
  const { setMobileActiveColumn } = useTaskUIStore();

  // Setup cleanup for this topic
  useTopicFiltersCleanup(topic.id);

  // View mode state (board or timeline) - this is UI state, not filter state
  const [viewMode, setViewMode] = useState<ViewMode>('board');

  // Track user filters separately since they have metadata beyond just IDs
  const [userFilters, setUserFilters] = useState<UserFilter[]>([]);

  // Access collaborators that were preloaded at the project level
  useEffect(() => {
    const accessCollaborators = async () => {
      if (!topic.project_id) return;
      try {
        // This will use the cache populated by preloadCollaborators
        const collaborators = await listCollaborators(topic.project_id);

        // Transform collaborators to UserFilter format
        const filters: UserFilter[] = collaborators
          .filter(collab => collab.users_with_details) // Skip any without user details
          .map(collab => ({
            id: collab.user_id,
            name: collab.users_with_details?.display_name || collab.users_with_details?.email || '',
            email: collab.users_with_details?.email || '',
            avatar_url: collab.users_with_details?.avatar_url,
            active: selectedUserIds.includes(collab.user_id), // Set active based on store state
          }));

        setUserFilters(filters);

        logger.component('Accessed cached collaborators', {
          projectId: topic.project_id,
          count: filters.length,
          hasCollaborators,
          filters,
        });
      } catch (error) {
        logger.error('Failed to access collaborators for filtering', {
          error: error instanceof Error ? error.message : String(error),
          projectId: topic.project_id,
        });
      }
    };

    // Access collaborators from cache
    accessCollaborators();
  }, [topic.project_id, selectedUserIds, hasCollaborators]);

  // Set up real-time subscriptions for tasks and task columns
  // Memoize subscriptions to prevent unnecessary re-subscriptions
  const subscriptions = useMemo(
    () => [
      {
        table: 'tasks' as const,
        queryKey: taskKeys.byTopic(topic.id),
      },
      {
        table: 'task_columns' as const,
        queryKey: taskColumnKeys.byTopic(topic.id),
      },
    ],
    [topic.id]
  );

  useTopicSubscription(topic.id, subscriptions);

  // Note: Tracking functions removed - using invalidation-based approach instead

  // Auto-select first column on mobile when columns load
  // Using a ref to ensure it only runs once after initial load
  const hasSetInitialColumn = React.useRef(false);
  useEffect(() => {
    if (isMobile && columns.length > 0 && !hasSetInitialColumn.current) {
      // Only run this once
      hasSetInitialColumn.current = true;

      // Sort columns by position to ensure we get the correct first column
      const sortedColumns = [...columns].sort((a, b) => a.position - b.position);
      if (sortedColumns[0]) {
        logger.component('Auto-selecting first column on mobile (initial only)', {
          status: sortedColumns[0].status,
          name: sortedColumns[0].name,
        });
        setMobileActiveColumn(sortedColumns[0].status);
      }
    }
  }, [isMobile, columns, setMobileActiveColumn]);

  // Memoize the computation for userIds to avoid recalculating on every render
  const toggleUserFilter = useCallback(
    (userId: string, isExclusive: boolean) => {
      // Get user's current active state
      const userFilter = userFilters.find(f => f.id === userId);
      const isActive = userFilter?.active || false;

      // Compute new user IDs based on toggle type
      let newUserIds: string[];
      if (isExclusive) {
        // Exclusive mode - only one or none selected
        newUserIds = isActive ? [] : [userId];
      } else {
        // Multi-select mode - toggle the selected ID
        const currentIds = [...selectedUserIds];
        if (isActive) {
          // Remove user ID if active
          newUserIds = currentIds.filter(id => id !== userId);
        } else {
          // Add user ID if not active and ensure no duplicates
          newUserIds = [...currentIds, userId];
        }
      }

      // Update filter state in store
      setSelectedUserIds(newUserIds);

      logger.component('Toggled user filter', {
        userId,
        isExclusive,
        newUserIds,
        wasActive: isActive,
        willBeActive: !isActive,
        topicId: topic.id,
      });
    },
    // Only depend on the data needed to determine the current state
    [userFilters, selectedUserIds, setSelectedUserIds]
  );

  // Memoize toggle view mode function to prevent it from changing on re-renders
  const toggleViewMode = useCallback(() => {
    setViewMode(prev => {
      const newMode = prev === 'board' ? 'timeline' : 'board';
      logger.component(`Switched to ${newMode} view`);
      return newMode;
    });
  }, []);

  // Calculate filtered tasks based on the UI filter state
  const filteredTasks = useMemo(() => {
    // Start with all tasks
    let result = [...tasks];

    // Filter by search query if present
    if (searchQuery) {
      const normalizedQuery = searchQuery.toLowerCase().trim();

      result = result.filter(task => {
        // Case-insensitive search on task title
        const titleMatch = task.title.toLowerCase().includes(normalizedQuery);

        // Also search in task description
        const descriptionMatch = task.description
          ? task.description.toLowerCase().includes(normalizedQuery)
          : false;

        return titleMatch || descriptionMatch;
      });
    }

    // Filter to ONLY show tasks WITHOUT deadlines if showUnscheduledOnly is checked
    if (showUnscheduledOnly) {
      result = result.filter(task => {
        // Check for null, undefined, empty string, or invalid date
        return !task.deadline || task.deadline === '';
      });
    }

    // Filter to ONLY show tasks WITHOUT assignees if showUnassignedOnly is checked
    if (showUnassignedOnly) {
      result = result.filter(task => {
        // Check for null, undefined, or empty string assignee
        return !task.assignedTo || task.assignedTo === '';
      });
    }

    // Filter to ONLY show highlighted tasks if showHighlightedOnly is checked
    if (showHighlightedOnly) {
      result = result.filter(task => task.isHighlighted === true);
    }

    // Filter by selected users if any are specified
    if (selectedUserIds.length > 0) {
      result = result.filter(task => task.assignedTo && selectedUserIds.includes(task.assignedTo));
    }

    // Filter by selected tags
    if (selectedTags.length > 0) {
      result = result.filter(task => {
        if (!task.tags) return false;

        // A task is included if it has ALL the selected tags (AND logic)
        return selectedTags.every(selectedTag => task.tags?.includes(selectedTag));
      });
    }

    // Sort by deadline if enabled
    if (sortByDeadline) {
      result = [...result].sort((a, b) => {
        // Tasks without deadlines go at the end
        if (!a.deadline && !b.deadline) return 0;
        if (!a.deadline) return 1;
        if (!b.deadline) return -1;

        // Sort by date
        return new Date(a.deadline).getTime() - new Date(b.deadline).getTime();
      });
    }

    return result;
  }, [
    tasks,
    searchQuery,
    showUnscheduledOnly,
    showUnassignedOnly,
    showHighlightedOnly,
    selectedUserIds,
    selectedTags,
    sortByDeadline,
  ]);

  // Use loading screen manager to mark content loaded when ready
  const { markRouteComplete } = useInitialLoadingScreenManager();

  // Only mark the topic content as loaded when both tasks and columns are loaded
  // Using a ref to track if we've already marked complete to avoid infinite updates
  const hasMarkedCompleteRef = useRef(false);
  useEffect(() => {
    if (!isTasksLoading && !isColumnsLoading && !hasMarkedCompleteRef.current) {
      // Content is loaded, mark it complete - but only once
      hasMarkedCompleteRef.current = true;
      markRouteComplete();
    }
  }, [isTasksLoading, isColumnsLoading]); // Remove markRouteComplete from deps to prevent infinite updates

  // Force viewMode back to 'board' if we're on mobile and somehow ended up in timeline view
  // This needs to be in useEffect to avoid setState during render
  useEffect(() => {
    if (viewMode === 'timeline' && isMobile) {
      setViewMode('board');
    }
  }, [viewMode, isMobile]);

  // Show loading state - only if tasks or columns are still loading
  if (isTasksLoading || isColumnsLoading) {
    // On desktop, use the simple skeleton loader with 3 columns
    if (!isMobile) {
      // TaskSkeleton already has the bg-[var(--content-bg-color)] class
      return <TaskSkeleton />;
    }

    // On mobile, use our task skeleton that matches desktop design
    // MobileTaskSkeleton already has the bg-[var(--content-bg-color)] class
    return <MobileTaskSkeleton />;
  }

  // Common filter toolbar element
  const filterToolbar = (
    <TaskFilterToolbar
      isMobile={isMobile}
      userFilters={userFilters}
      toggleUserFilter={toggleUserFilter}
      viewMode={viewMode}
      toggleViewMode={toggleViewMode}
      hasCollaborators={hasCollaborators}
      topicId={topic.id}
      // Passing props needed for TaskFilterToolbar
      selectedTags={selectedTags}
      setSelectedTags={setSelectedTags}
      showUnscheduledOnly={showUnscheduledOnly}
      toggleUnscheduledOnly={toggleUnscheduledOnly}
      showUnassignedOnly={showUnassignedOnly}
      toggleUnassignedOnly={toggleUnassignedOnly}
      showHighlightedOnly={showHighlightedOnly}
      toggleHighlightedOnly={toggleHighlightedOnly}
      sortByDeadline={sortByDeadline}
      toggleSortByDeadline={toggleSortByDeadline}
      resetFilters={resetFilters}
      availableTags={Array.from(new Set(tasks.flatMap(task => task.tags || [])))}
    />
  );

  // If in timeline view, render the timeline (only on desktop)
  if (viewMode === 'timeline' && !isMobile) {
    return (
      <TaskTimelineView
        topicId={topic.id}
        tasks={filteredTasks as unknown as FormattedTask[]}
        isMobile={false}
        filterHeader={filterToolbar}
      />
    );
  }

  // If in board view, render the appropriate board based on device
  if (isMobile) {
    return (
      <div className="h-full flex flex-col relative overflow-hidden">
        <div className="flex-1 overflow-x-visible">
          <MobileTaskBoard
            topicName={topic.name}
            topicId={topic.id}
            projectId={topic.project_id}
            tasks={filteredTasks as unknown as FormattedTask[]}
            onBackClick={onBackClick}
            hasCollaborators={hasCollaborators}
            userFilters={userFilters}
            toggleUserFilter={toggleUserFilter}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="h-full flex flex-col relative">
      {filterToolbar}
      <div className="flex-1 overflow-hidden">
        <DndProvider backend={HTML5Backend}>
          <DndTaskBoard
            topicId={topic.id}
            projectId={topic.project_id}
            tasks={filteredTasks as unknown as FormattedTask[]}
            hasCollaborators={hasCollaborators}
            showUnscheduledOnly={showUnscheduledOnly}
            showUnassignedOnly={showUnassignedOnly}
            userFilters={userFilters}
            sortByDeadline={sortByDeadline}
          />
        </DndProvider>
      </div>
    </div>
  );
}

// Export the memoized component wrapped with error boundary and filter provider
const MemoizedTaskTopicContent = React.memo(TaskTopicContent);

export const TaskTopic: React.FC<TaskTopicProps> = props => (
  <RealtimeErrorBoundary
    maxRetries={3}
    fallback={MinimalRealtimeFallback}
    onError={(error, errorInfo) => {
      // Log error for monitoring/analytics
      logger.error('TaskTopic realtime error boundary triggered', {
        error: error.message,
        topicId: props.topic.id,
        componentStack: errorInfo.componentStack,
      });
    }}
  >
    <TopicFiltersProvider topicId={props.topic.id}>
      <MemoizedTaskTopicContent {...props} />
    </TopicFiltersProvider>
  </RealtimeErrorBoundary>
);
